import axios from "axios";
import { getSession } from "../Utils/SessionUtils";

const api_url = process.env.REACT_APP_API;

// Helper function to get dynamic config with current token
const getConfig = () => {
  const token = getSession("token");
  console.log(
    "Current token in getConfig:",
    token ? "Token exists" : "No token"
  );

  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
};

//to add in every api
const currentOrg = getSession("usedorganisations");

/**
 * Get all cash journals for an organisation
 * @param {string} organisationId - Organisation ID (optional, uses current org if not provided)
 * @returns {Promise} Axios response
 */
export async function getAllCashJournals(
  organisationId = null,
  startDate,
  endDate
) {
  const orgId = organisationId || currentOrg;

  try {
    const response = await axios.get(
      api_url + `journal-caisse/organisation/${orgId}/${startDate}/${endDate}`,
      getConfig()
    );
    return response;
  } catch (error) {
    console.error("Error fetching cash journals:", error);
    throw error;
  }
}

/**
 * Get active cash journal for an organisation
 * @param {string} organisationId - Organisation ID (optional, uses current org if not provided)
 * @returns {Promise} Axios response
 */
export async function getActiveCashJournal(organisationId = null) {
  const orgId = organisationId || currentOrg;

  try {
    const response = await axios.get(
      api_url + "journal-caisse/organisation/" + orgId + "/active",
      getConfig()
    );
    return response;
  } catch (error) {
    console.error("Error fetching active cash journal:", error);
    throw error;
  }
}
/**
 * Get active cash journal for an organisation
 * @param {string} organisationId - Organisation ID (optional, uses current org if not provided)
 * @returns {Promise} Axios response
 */
export async function getJournalReport(journalId) {
  try {
    const response = await axios.get(
      api_url + "journal-caisse/report/" + journalId,
      getConfig()
    );
    return response;
  } catch (error) {
    console.error("Error fetching active cash journal:", error);
    throw error;
  }
}
/**
 * Get cash journal by ID
 * @param {string} journalId - Cash journal ID
 * @returns {Promise} Axios response
 */
export async function getCashJournalById(journalId) {
  try {
    const response = await axios.get(
      api_url + "journal-caisse/" + journalId,
      getConfig()
    );
    return response;
  } catch (error) {
    console.error("Error fetching cash journal by ID:", error);
    throw error;
  }
}

/**
 * Create new cash journal
 * @param {Object} journalData - Journal data
 * @param {number} journalData.fond_initial - Initial fund amount
 * @param {string} journalData.user_id - User ID who creates the journal
 * @param {string} journalData.validator_id - Validator ID (optional)
 * @param {Date} journalData.start_date - Start date (optional, defaults to now)
 * @param {string} organisationId - Organisation ID (optional, uses current org if not provided)
 * @returns {Promise} Axios response
 */
export async function createCashJournal(journalData, organisationId = null) {
  const orgId = organisationId || currentOrg;

  const body = {
    fond_initial: journalData.fond_initial,
    user_id: journalData.user_id,
    validator_id: journalData.validator_id || null,
    start_date: journalData.start_date || new Date(),
  };

  try {
    const response = await axios.post(
      api_url + "journal-caisse/organisation/" + orgId,
      body,
      getConfig()
    );
    return response;
  } catch (error) {
    console.error("Error creating cash journal:", error);
    throw error;
  }
}

/**
 * Update cash journal - FIXED VERSION
 * @param {string} journalId - Cash journal ID
 * @param {Object} updateData - Update data
 * @param {number} updateData.fond_initial - Initial fund amount (optional)
 * @param {number} updateData.fond_courant - Current fund amount (optional)
 * @param {string} updateData.validator_id - Validator ID (optional)
 * @returns {Promise} Axios response
 */
export async function updateCashJournal(journalId, updateData) {
  const body = {
    ...updateData, // Send all update data including fond_initial
  };

  try {
    const response = await axios.patch(
      api_url + `journal-caisse/${journalId}`,
      body,
      getConfig()
    );
    console.log("Update response:", response.data);
    return response;
  } catch (error) {
    console.error("Error updating cash journal:", error);
    console.error("Error details:", error.response?.data || error.message);
    throw error;
  }
}

/**
 * Close cash journal
 * @param {string} journalId - Cash journal ID
 * @param {Object} closeData - Close data
 * @param {number} closeData.fond_final - Final fund amount
 * @param {string} closeData.validator_id - Validator ID
 * @param {Date} closeData.end_date - End date (optional, defaults to now)
 * @returns {Promise} Axios response
 */
export async function closeCashJournal(journalId, closeData) {
  const body = {
    fond_final: closeData.fond_final,
    validator_id: closeData.validator_id,
    end_date: closeData.end_date || new Date(),
  };

  try {
    const response = await axios.put(
      api_url + "journal-caisse/" + journalId + "/close",
      body,
      getConfig()
    );
    return response;
  } catch (error) {
    console.error("Error closing cash journal:", error);
    throw error;
  }
}

/**
 * Update current fund (add or subtract)
 * @param {string} journalId - Cash journal ID
 * @param {number} amount - Amount to add/subtract
 * @param {string} operation - Operation type: 'add' or 'subtract'
 * @returns {Promise} Axios response
 */
export async function updateCurrentFund(journalId, amount, operation) {
  const body = {
    amount: amount,
    operation: operation,
  };

  try {
    const response = await axios.patch(
      api_url + "journal-caisse/" + journalId + "/fund",
      body,
      getConfig()
    );
    return response;
  } catch (error) {
    console.error("Error updating current fund:", error);
    throw error;
  }
}

/**
 * Add to current fund
 * @param {string} journalId - Cash journal ID
 * @param {number} amount - Amount to add
 * @returns {Promise} Axios response
 */
export async function addToFund(journalId, amount) {
  return await updateCurrentFund(journalId, amount, "add");
}

/**
 * Subtract from current fund
 * @param {string} journalId - Cash journal ID
 * @param {number} amount - Amount to subtract
 * @returns {Promise} Axios response
 */
export async function subtractFromFund(journalId, amount) {
  return await updateCurrentFund(journalId, amount, "subtract");
}

/**
 * Delete cash journal
 * @param {string} journalId - Cash journal ID
 * @returns {Promise} Axios response
 */
export async function deleteCashJournal(journalId) {
  try {
    const response = await axios.delete(
      api_url + "journal-caisse/" + journalId,
      getConfig()
    );
    return response;
  } catch (error) {
    console.error("Error deleting cash journal:", error);
    throw error;
  }
}

/**
 * Get cash journal statistics for an organisation
 * @param {string} organisationId - Organisation ID (optional, uses current org if not provided)
 * @returns {Promise} Object with statistics
 */
export async function getCashJournalStats(organisationId = null) {
  const orgId = organisationId || currentOrg;

  try {
    const journalsResponse = await getAllCashJournals(orgId);

    if (journalsResponse.data && journalsResponse.data.data) {
      const journals = journalsResponse.data.data;

      const stats = {
        totalJournals: journals.length,
        activeJournals: journals.filter((j) => !j.cloture).length,
        closedJournals: journals.filter((j) => j.cloture).length,
        totalInitialFund: journals.reduce(
          (sum, j) => sum + (j.fond_initial || 0),
          0
        ),
        totalCurrentFund: journals
          .filter((j) => !j.cloture)
          .reduce((sum, j) => sum + (j.fond_courant || 0), 0),
        totalFinalFund: journals
          .filter((j) => j.cloture)
          .reduce((sum, j) => sum + (j.fond_final || 0), 0),
      };

      return { data: { success: "1", data: stats } };
    }

    return { data: { success: "0", message: "No data available" } };
  } catch (error) {
    console.error("Error fetching cash journal statistics:", error);
    throw error;
  }
}

/**
 * Check if organisation has active cash journal
 * @param {string} organisationId - Organisation ID (optional, uses current org if not provided)
 * @returns {Promise<boolean>} True if active journal exists
 */
export async function hasActiveCashJournal(organisationId = null) {
  try {
    const response = await getActiveCashJournal(organisationId);
    return response.data && response.data.success !== "0";
  } catch (error) {
    // If 404 error, no active journal exists
    if (error.response && error.response.status === 404) {
      return false;
    }
    console.error("Error checking active cash journal:", error);
    throw error;
  }
}
